import React, { useState, useEffect, useCallback } from "react";
import { Stack, Typography, Divider, Box, Button, Chip, Tabs, Tab, Dialog, DialogTitle, DialogContent, IconButton, Tooltip, Snackbar, Alert } from "@mui/material";
import { ArrowBack, Person, BusinessCenter, Paid, Security, NoteAdd, Policy, ExitToApp, Close } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData, postData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import Loader from "../../../common/spinner";
import PersonalContactDetails from "./employeeDetails/personalContactDetails";
import EmploymentDetails from "./employeeDetails/employmentDetails";
import CompensationPay from "./employeeDetails/compensationPay";
import BankingStatutoryDetails from "./employeeDetails/bankingStatutoryDetails";
import EmployeeDocuments from "./employeeDetails/employeeDocuments";
import AssignPolicies from "./employeeDetails/assignPolicies";
import ExitRehire from "./employeeDetails/exitRehire";
import ViewAllDetailsModal from "./employeeDetails/viewAllDetailsModal";

function EmployeeDetailsView() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const empId = searchParams.get("employeeId");
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const tabFromParams = parseInt(searchParams.get('tab'), 10);
    const initialTab = isNaN(tabFromParams) ? 0 : tabFromParams;
    const [activeTab, setActiveTab] = useState(initialTab);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [navigationContext, setNavigationContext] = useState(null);
    const [rehireChecklist, setRehireChecklist] = useState({
        personalContact: false,
        employment: false,
        compensationPay: false,
        bankingStatutory: false,
        documents: false,
        assignPolicies: false,
    });
    const [sendingEmail, setSendingEmail] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success", // 'success' | 'error' | 'info' | 'warning'
    });

    const stepToTabIndex = {
        personalContact: 0,
        employment: 1,
        compensationPay: 2,
        bankingStatutory: 3,
        documents: 4,
        assignPolicies: 5,
    };

    const handleStartRehireVerification = (stepKey, stepLabel) => {
        const tabIndex = stepToTabIndex[stepKey];
        if (tabIndex === undefined) return;
        setNavigationContext({ returnToRehire: true, stepKey, stepLabel });
        setActiveTab(tabIndex);
    };

    const handleVerifyAndReturn = () => {
        if (!navigationContext?.stepKey) {
            setNavigationContext(null);
            setActiveTab(6);
            return;
        }

        setRehireChecklist(prev => ({
            ...prev,
            [navigationContext.stepKey]: true,
        }));
        setNavigationContext(null);
        setActiveTab(6);
    };

    const handleUpdateRehireChecklist = (updatedChecklist) => {
        setRehireChecklist(updatedChecklist);
    };

    const fetchUserDetails = useCallback(async () => {
        if (!empId) return;
        setLoading(true);
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${empId}`);
            if (response?.status === "SUCCESS") {
                setUserDetails(response.message.user);
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [empId]);

    useEffect(() => {
        fetchUserDetails();
    }, [fetchUserDetails]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        const pendingAction = localStorage.getItem("openEmployeeManagementEmployee");
        if (pendingAction) {
            const data = JSON.parse(pendingAction);
            const urlParams = new URLSearchParams(window.location.search);
            const currentId = urlParams.get('employeeId');

            if (data.employeeId.toString() === currentId) {
                setActiveTab(2);
                localStorage.removeItem("openEmployeeManagementEmployee");
            }
        }
    }, []);

    const handleResendVerification = async () => {
        if (!userDetails?.email) return;

        setSendingEmail(true);
        try {
            const payload = { email: userDetails.email };
            const response = await postData(API_ENDPOINTS.resendEmailVerification, payload);

            if (response?.status === "SUCCESS") {
                setSnackbar({
                    open: true,
                    message: `Verification link successfully sent to ${userDetails.email}`,
                    severity: "success",
                });
            } else {
                setSnackbar({
                    open: true,
                    message: response?.message || "Failed to send verification link.",
                    severity: "error",
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: "An error occurred while sending the verification link.",
                severity: "error",
            });
        } finally {
            setSendingEmail(false);
        }
    };

    if (loading) return <Loader />;
    if (!userDetails) return <Box sx={{ p: 3 }}>User not found.</Box>;

    return (
        <Box>
            {/* MOBILE HEADER — xs only */}
            <Box sx={{ display: { xs: 'block', sm: 'none' }, borderBottom: '1px solid #c3c3c3', py: 1.5, px: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Button
                        onClick={() => navigate(-1)}
                        startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                        sx={{ color: '#64748b', borderRadius: '8px', textTransform: 'none', px: 1, '&:hover': { bgcolor: 'rgba(100, 116, 139, 0.04)' } }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>Back</Typography>
                    </Button>

                    <Button
                        variant="contained"
                        size="small"
                        sx={{ textTransform: 'none', borderRadius: '6px', px: 1.5, boxShadow: 'none', fontSize: '0.75rem', '&:hover': { boxShadow: 'none' } }}
                        onClick={() => setIsModalOpen(true)}
                    >
                        View
                    </Button>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1rem' }}>
                        {userDetails.first_name} {userDetails.last_name}
                    </Typography>

                    {userDetails.is_verified && (
                        <Tooltip title="Email Verified">
                            <Security sx={{ fontSize: 18, color: '#10b981', flexShrink: 0 }} />
                        </Tooltip>
                    )}
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" noWrap sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.65rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Emp ID: {userDetails.emp_id} • {userDetails.email}
                    </Typography>

                    <Chip
                        label={userDetails.status}
                        size="small"
                        sx={{
                            fontSize: '10px',
                            height: '20px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            borderRadius: '24px',
                            color: userDetails.status?.toLowerCase() === 'active' ? '#10b981' : '#ef4444',
                            bgcolor: userDetails.status?.toLowerCase() === 'active' ? '#ecfdf5' : '#fef2f2',
                            border: '1px solid currentColor',
                        }}
                    />
                </Stack>
            </Box>

            {/* DESKTOP/TABLET HEADER — sm and up */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{ display: { xs: 'none', sm: 'flex' }, borderBottom: '1px solid #c3c3c3', py: 1.5, px: 1 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                    {/* Use Button instead of IconButton to allow Text + Icon nesting safely */}
                    <Button
                        onClick={() => navigate(-1)}
                        startIcon={<ArrowBack sx={{ fontSize: 20 }} />}
                        sx={{
                            color: '#64748b',
                            borderRadius: '8px',
                            textTransform: 'none',
                            flexShrink: 0,
                            '&:hover': { bgcolor: 'rgba(100, 116, 139, 0.04)' }
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Back</Typography>
                    </Button>

                    <Divider orientation="vertical" flexItem sx={{ mx: 3, height: 40, alignSelf: 'center' }} />

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {userDetails.first_name} {userDetails.last_name}
                            </Typography>

                            {/* Email Verification Status */}
                            {userDetails.is_verified ? (
                                <Tooltip title="Email Verified">
                                    <Security sx={{ fontSize: 18, color: '#10b981' }} />
                                </Tooltip>
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    // Apply the variable here to fix the ESLint warning
                                    disabled={sendingEmail}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        py: 0,
                                        height: '20px',
                                        '&:hover': { bgcolor: '#fef2f2' },
                                        // Visual feedback for disabled state
                                        '&.Mui-disabled': {
                                            borderColor: '#f8d7da',
                                            color: '#f8d7da'
                                        }
                                    }}
                                    onClick={handleResendVerification}
                                >
                                    {/* Dynamic text based on state */}
                                    {sendingEmail ? "Sending..." : "Resend Verification Link"}
                                </Button>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                                Emp ID: {userDetails.emp_id} • {userDetails.email}
                            </Typography>

                            <Chip
                                label={userDetails.status}
                                size="small"
                                sx={{
                                    fontSize: '10px',
                                    height: '20px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    borderRadius: '24px',
                                    color: userDetails.status?.toLowerCase() === 'active' ? '#10b981' : '#ef4444',
                                    bgcolor: userDetails.status?.toLowerCase() === 'active' ? '#ecfdf5' : '#fef2f2',
                                    border: '1px solid currentColor',
                                }}
                            />
                        </Stack>
                    </Box>
                </Box>

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        size="small"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '6px',
                            px: 2,
                            boxShadow: 'none',
                            '&:hover': { boxShadow: 'none' }
                        }}
                        onClick={() => setIsModalOpen(true)}
                    >
                        View All Details
                    </Button>
                </Stack>
            </Stack>

            <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #c3c3c3', borderRadius: '12px', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)', p: 1, mt: 1 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTabs-scrollButtons': {
                            width: { xs: 28, sm: 36 },
                            color: '#1976d2',
                            '&.Mui-disabled': { opacity: 0.3 },
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            minWidth: { xs: 100, sm: 160 },
                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                            fontWeight: 600,
                            color: '#64748b',
                            py: { xs: 1, sm: 2 },
                            borderRadius: '8px 8px 0 0',
                            transition: '0.2s',
                            '&.Mui-selected': {
                                color: '#1976d2',
                                bgcolor: '#e3f2fd',
                            },
                        }
                    }}
                >
                    <Tab icon={<Person sx={{ fontSize: { xs: 16, sm: 20 } }} />} label="Personal & Contact" />
                    <Tab icon={<BusinessCenter sx={{ fontSize: { xs: 16, sm: 20 } }} />} label="Employment Details" />
                    <Tab icon={<Paid sx={{ fontSize: { xs: 16, sm: 20 } }} />} label="Compensation & Pay" />
                    <Tab icon={<Security sx={{ fontSize: { xs: 16, sm: 20 } }} />} label="Banking & Statutory" />
                    <Tab icon={<NoteAdd sx={{ fontSize: { xs: 16, sm: 20 } }} />} label="Documents & Other" />
                    <Tab icon={<Policy sx={{ fontSize: { xs: 16, sm: 20 } }} />} label="Assign Policies" />
                    <Tab icon={<ExitToApp sx={{ fontSize: { xs: 16, sm: 20 } }} />} label="Exit / Rehire" />
                </Tabs>
            </Box>

            <Box>
                {activeTab === 0 && (
                    <PersonalContactDetails
                        navigationContext={navigationContext}
                        onSaveAndReturn={handleVerifyAndReturn}
                    />
                )}
                {activeTab === 1 && (
                    <EmploymentDetails
                        navigationContext={navigationContext}
                        onSaveAndReturn={handleVerifyAndReturn}
                    />
                )}
                {activeTab === 2 && (
                    <CompensationPay
                        navigationContext={navigationContext}
                        onSaveAndReturn={handleVerifyAndReturn}
                    />
                )}
                {activeTab === 3 && (
                    <BankingStatutoryDetails
                        navigationContext={navigationContext}
                        onSaveAndReturn={handleVerifyAndReturn}
                    />
                )}
                {activeTab === 4 && (
                    <EmployeeDocuments
                        navigationContext={navigationContext}
                        onSaveAndReturn={handleVerifyAndReturn}
                    />
                )}
                {activeTab === 5 && (
                    <AssignPolicies
                        navigationContext={navigationContext}
                        onSaveAndReturn={handleVerifyAndReturn}
                    />
                )}
                {activeTab === 6 && (
                    <ExitRehire
                        navigationContext={navigationContext}
                        rehireChecklist={rehireChecklist}
                        onUpdateRehireChecklist={handleUpdateRehireChecklist}
                        onStartRehireVerification={handleStartRehireVerification}
                        onRehireCompleted={fetchUserDetails}
                    />
                )}
            </Box>
            <Dialog
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Change component to "span" to avoid nesting <h6> inside <h2> */}
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        Profile Summary
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <Button variant="outlined" startIcon={<GetAppIcon />} sx={{ textTransform: 'none' }}>Download</Button> */}
                        <IconButton
                            onClick={() => setIsModalOpen(false)}
                            sx={{ color: (theme) => theme.palette.grey[500] }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <ViewAllDetailsModal
                        onClose={() => setIsModalOpen(false)}
                        userDetails={userDetails}
                    />
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // Top-right is standard for dashboards
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled" // Optional: makes it more prominent
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default EmployeeDetailsView;